import React, { useEffect, useState } from 'react';
import {
    Button,
    Col,
    Form,
    Input,
    InputNumber,
    Row,
    Switch,
    Typography,
    notificationProvider,
} from '@pankod/refine-antd';

const { Text } = Typography;

interface PlatformConfig {
    id: string;
    nrEnabled: boolean;
    nrReplaySampleRate: number;
    lastUpdateNotes: string | null;
    updatedAt: string;
    updatedBy?: { id: string; user?: { name?: string; email?: string } } | null;
}

const API_URL = process.env.REACT_APP_URL ?? '';
const ENDPOINT = `${API_URL}/config/platform-config/internal-portal`;

function getAuthHeaders(): HeadersInit {
    const token = sessionStorage.getItem('konsole-token');
    return {
        'Content-Type': 'application/json',
        authorization: token ? `Bearer ${token}` : '',
    };
}

export const PlatformConfigPanel: React.FC = () => {
    const [form] = Form.useForm();
    const [config, setConfig] = useState<PlatformConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetch(ENDPOINT, { headers: getAuthHeaders() })
            .then((res) => res.json())
            .then((data: PlatformConfig) => {
                setConfig(data);
                form.setFieldsValue({
                    nrEnabled: data.nrEnabled,
                    nrReplaySampleRate: Number(data.nrReplaySampleRate),
                    lastUpdateNotes: '',
                });
            })
            .catch(() => {
                notificationProvider.open({
                    type: 'error',
                    message: 'Failed to load platform config',
                    description: 'Could not fetch current config from server.',
                });
            })
            .finally(() => setLoading(false));
    }, [form]);

    const handleSave = async (values: Partial<PlatformConfig> & { lastUpdateNotes?: string }) => {
        setSaving(true);
        try {
            const res = await fetch(ENDPOINT, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(values),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: PlatformConfig = await res.json();
            setConfig(updated);
            form.setFieldsValue({ lastUpdateNotes: '' });
            notificationProvider.open({
                type: 'success',
                message: 'Platform config updated',
                description: 'Saved. Config is cached for 10 minutes — users will see the change on their next page refresh after the cache expires.',
            });
        } catch (err: any) {
            notificationProvider.open({
                type: 'error',
                message: 'Failed to update platform config',
                description: err?.message ?? 'Unknown error',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form form={form} layout="vertical" onFinish={handleSave}>
            <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                    <Form.Item label="NR enabled" name="nrEnabled" valuePropName="checked">
                        <Switch disabled={loading} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="NR replay sample rate (0–1)"
                        name="nrReplaySampleRate"
                        rules={[
                            { type: 'number', min: 0, max: 1, message: 'Must be between 0 and 1' },
                        ]}
                    >
                        <InputNumber
                            min={0}
                            max={1}
                            step={0.01}
                            precision={3}
                            style={{ width: '100%' }}
                            disabled={loading}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24}>
                    <Form.Item
                        label="Reason for change"
                        name="lastUpdateNotes"
                        rules={[{ required: true, message: 'Please enter a reason' }]}
                    >
                        <Input.TextArea rows={2} disabled={loading} />
                    </Form.Item>
                </Col>
            </Row>
            {config && (
                <Row style={{ marginBottom: 8 }}>
                    <Col xs={24}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Last updated: {new Date(config.updatedAt).toLocaleString()}
                            {config.updatedBy?.user?.email ? ` by ${config.updatedBy.user.email}` : ''}
                        </Text>
                    </Col>
                </Row>
            )}
            <Row>
                <Col xs={24} style={{ textAlign: 'right' }}>
                    <Button type="primary" htmlType="submit" loading={saving} disabled={loading}>
                        Save
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};
